import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar, Clock, LogOut, Send, FileText, Download, 
  Bell, MessageSquare, Heart, Image as ImageIcon, RefreshCw, History, Eye,
  Home, ChevronRight, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import './EmployeePortal.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployeePortal = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [pastSchedules, setPastSchedules] = useState([]);
  const [myTimeoffRequests, setMyTimeoffRequests] = useState([]);
  const [myShiftSwapRequests, setMyShiftSwapRequests] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [posts, setPosts] = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showShiftSwapForm, setShowShiftSwapForm] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [postImage, setPostImage] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentText, setCommentText] = useState({});
  
  const [timeoffRequest, setTimeoffRequest] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });

  const [shiftSwapRequest, setShiftSwapRequest] = useState({
    shift_date: '',
    shift_time: '',
    swap_with: '',
    notes: ''
  });

  const navigate = useNavigate();

  const [fetchError, setFetchError] = useState(null);

  const loadSchedule = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/employee/schedule/current`, {
        withCredentials: true
      });
      setCurrentSchedule(response.data.schedule);
    } catch (err) {
      console.error('Failed to load schedule:', err.message);
      setFetchError('Unable to load schedule');
    }
  }, []);

  const loadPastSchedules = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/employee/schedule/history`, {
        withCredentials: true
      });
      setPastSchedules(response.data.schedules || []);
    } catch (err) {
      console.error('Failed to load past schedules:', err.message);
      setFetchError('Unable to load past schedules');
    }
  }, []);

  const loadMyRequests = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/employee/timeoff`, {
        withCredentials: true
      });
      setMyTimeoffRequests(response.data.requests);
    } catch (err) {
      console.error('Failed to load time-off requests:', err.message);
      setFetchError('Unable to load requests');
    }
  }, []);

  const loadMyShiftSwaps = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/employee/shift-swap`, {
        withCredentials: true
      });
      setMyShiftSwapRequests(response.data.requests);
    } catch (err) {
      console.error('Failed to load shift swaps:', err.message);
      setFetchError('Unable to load shift swaps');
    }
  }, []);

  const loadAnnouncements = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/announcements`, {
        withCredentials: true
      });
      setAnnouncements(response.data.announcements);
    } catch (err) {
      console.error('Failed to load announcements:', err.message);
      setFetchError('Unable to load announcements');
    }
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await axios.get(`${API}/feed/posts`, {
        withCredentials: true
      });
      setPosts(response.data.posts);
    } catch (err) {
      console.error('Failed to load posts:', err.message);
      setFetchError('Unable to load posts');
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      
      setUser(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Auth check failed:', err.message);
      navigate('/team');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) {
      if (activeTab === 'home') {
        loadSchedule();
        loadAnnouncements();
      } else if (activeTab === 'schedule') {
        loadSchedule();
        loadPastSchedules();
      } else if (activeTab === 'timeoff') {
        loadMyRequests();
      } else if (activeTab === 'shiftswap') {
        loadMyShiftSwaps();
      } else if (activeTab === 'announcements') {
        loadAnnouncements();
      } else if (activeTab === 'feed') {
        loadPosts();
      }
    }
  }, [activeTab, user, loadSchedule, loadPastSchedules, loadMyRequests, loadMyShiftSwaps, loadAnnouncements, loadPosts]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (err) {
      console.error('Logout request failed:', err);
    } finally {
      navigate('/team');
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employee/timeoff`, timeoffRequest, {
        withCredentials: true
      });
      
      toast.success('Time off request submitted');
      setTimeoffRequest({ start_date: '', end_date: '', reason: '' });
      setShowRequestForm(false);
      loadMyRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleShiftSwapSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employee/shift-swap`, shiftSwapRequest, {
        withCredentials: true
      });
      
      toast.success('Shift swap request submitted');
      setShiftSwapRequest({ shift_date: '', shift_time: '', swap_with: '', notes: '' });
      setShowShiftSwapForm(false);
      loadMyShiftSwaps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    }
  };

  const downloadSchedule = async (schedule) => {
    const target = schedule || currentSchedule;
    if (!target) return;
    
    try {
      const response = await axios.get(
        `${API}/employee/schedule/file/${target.id}`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', target.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download schedule');
    }
  };

  const viewSchedule = (schedule) => {
    const target = schedule || currentSchedule;
    if (!target) return;
    window.open(
      `${API}/employee/schedule/file/${target.id}?disposition=inline`,
      '_blank',
      'noopener'
    );
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    
    const formData = new FormData();
    if (postContent) formData.append('content', postContent);
    if (postImage) formData.append('image', postImage);

    try {
      await axios.post(`${API}/feed/posts`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPostContent('');
      setPostImage(null);
      loadPosts();
      toast.success('Post published');
    } catch (err) {
      toast.error('Failed to create post');
    }
  };

  const handleToggleLike = async (postId) => {
    try {
      await axios.post(`${API}/feed/posts/${postId}/like`, {}, {
        withCredentials: true
      });
      loadPosts();
    } catch (err) {
      console.error('Failed to toggle like');
    }
  };

  const handleAddComment = async (postId) => {
    const content = commentText[postId];
    if (!content?.trim()) return;

    try {
      await axios.post(`${API}/feed/posts/${postId}/comments`, 
        { content },
        { withCredentials: true }
      );
      
      setCommentText({ ...commentText, [postId]: '' });
      loadComments(postId);
      toast.success('Comment added');
    } catch (err) {
      toast.error('Failed to add comment');
    }
  };

  const loadComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/feed/posts/${postId}/comments`, {
        withCredentials: true
      });
      
      setExpandedComments({
        ...expandedComments,
        [postId]: response.data.comments
      });
    } catch (err) {
      console.error('Failed to load comments');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (changingPassword) return;
    if (passwordForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New password and confirmation do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/employee/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowChangePassword(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return <div className="employee-loading">Loading...</div>;
  }

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();
  const firstName = user?.name?.split(' ')[0] || 'there';
  const pastOnly = pastSchedules.filter((s) => !s.is_current);

  return (
    <div className="employee-portal">
      <div className="employee-header">
        <div className="employee-header-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_elegant-royal-bar/artifacts/6ywx5xvf_CFB2E630-E067-4D50-B2DB-A530D292F3D8.png" 
            alt="Royal Bar & Grill" 
            className="employee-logo"
          />
          <div className="employee-user-info">
            <span>Welcome, {user?.name}</span>
            <button onClick={handleLogout} className="btn-logout">
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="employee-container">
        <div className="employee-tabs" data-testid="employee-tabs">
          <button 
            className={`employee-tab ${activeTab === 'home' ? 'active' : ''}`}
            onClick={() => setActiveTab('home')}
            data-testid="tab-home"
          >
            <Home size={20} />
            Home
          </button>
          <button 
            className={`employee-tab ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
            data-testid="tab-schedule"
          >
            <Calendar size={20} />
            My Schedule
          </button>
          <button 
            className={`employee-tab ${activeTab === 'timeoff' ? 'active' : ''}`}
            onClick={() => setActiveTab('timeoff')}
            data-testid="tab-timeoff"
          >
            <Clock size={20} />
            Time Off
          </button>
          <button 
            className={`employee-tab ${activeTab === 'shiftswap' ? 'active' : ''}`}
            onClick={() => setActiveTab('shiftswap')}
            data-testid="tab-shiftswap"
          >
            <RefreshCw size={20} />
            Shift Swap
          </button>
          <button 
            className={`employee-tab ${activeTab === 'announcements' ? 'active' : ''}`}
            onClick={() => setActiveTab('announcements')}
            data-testid="tab-announcements"
          >
            <Bell size={20} />
            Announcements
          </button>
          <button 
            className={`employee-tab ${activeTab === 'feed' ? 'active' : ''}`}
            onClick={() => setActiveTab('feed')}
            data-testid="tab-feed"
          >
            <MessageSquare size={20} />
            Team Feed
          </button>
        </div>

        <div className="employee-content">
          <div className="employee-main-content">

          {/* HOME DASHBOARD TAB */}
          {activeTab === 'home' && (
            <div className="dashboard-section" data-testid="employee-dashboard">
              <div className="dashboard-welcome">
                <p className="dashboard-greeting">{greeting}, {firstName}</p>
                <h2 className="dashboard-title">Welcome to your portal</h2>
                <p className="dashboard-date">
                  {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
              </div>

              <div className="dashboard-cards">
                <div className="dash-card" data-testid="dash-schedule-card">
                  <div className="dash-card-head">
                    <Calendar size={18} />
                    <h3>This Week's Schedule</h3>
                  </div>
                  {currentSchedule ? (
                    <div className="dash-card-body">
                      <p className="dash-card-main">{currentSchedule.filename}</p>
                      <p className="dash-card-sub">{new Date(currentSchedule.week_start).toLocaleDateString()}{currentSchedule.week_end ? ` - ${new Date(currentSchedule.week_end).toLocaleDateString()}` : ''}</p>
                      <button className="dash-card-action" onClick={() => viewSchedule(currentSchedule)} data-testid="dash-view-schedule">
                        <Eye size={16} /> View Schedule
                      </button>
                    </div>
                  ) : (
                    <div className="dash-card-body">
                      <p className="dash-card-empty">No current schedule available.</p>
                    </div>
                  )}
                </div>

                <div className="dash-card" data-testid="dash-announcement-card">
                  <div className="dash-card-head">
                    <Bell size={18} />
                    <h3>Latest Announcement</h3>
                  </div>
                  {announcements.length > 0 ? (
                    <div className="dash-card-body">
                      <p className="dash-card-main">{announcements[0].title}</p>
                      <p className="dash-card-sub dash-clamp">{announcements[0].message}</p>
                      <button className="dash-card-action" onClick={() => setActiveTab('announcements')} data-testid="dash-view-announcements">
                        View all <ChevronRight size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="dash-card-body">
                      <p className="dash-card-empty">No announcements yet</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="dashboard-quick">
                <h3 className="quick-title">Quick Actions</h3>
                <div className="quick-actions">
                  <button className="quick-btn" onClick={() => setActiveTab('schedule')} data-testid="quick-view-schedule">
                    <span className="quick-icon"><Calendar size={24} /></span>
                    <span>View Schedule</span>
                  </button>
                  <button className="quick-btn" onClick={() => { setActiveTab('timeoff'); setShowRequestForm(true); }} data-testid="quick-request-timeoff">
                    <span className="quick-icon"><Clock size={24} /></span>
                    <span>Request Time Off</span>
                  </button>
                  <button className="quick-btn" onClick={() => { setActiveTab('shiftswap'); setShowShiftSwapForm(true); }} data-testid="quick-request-swap">
                    <span className="quick-icon"><RefreshCw size={24} /></span>
                    <span>Request Shift Swap</span>
                  </button>
                  <button className="quick-btn" onClick={() => setShowChangePassword(true)} data-testid="quick-change-password">
                    <span className="quick-icon"><KeyRound size={24} /></span>
                    <span>Change Password</span>
                  </button>
                </div>
              </div>

              {showChangePassword && (
                <div className="request-form-card" data-testid="change-password-form">
                  <h3>Change Password</h3>
                  <form onSubmit={handleChangePassword} className="request-form">
                    <div className="form-group">
                      <label>Current Password *</label>
                      <input
                        type="password"
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                        required
                        placeholder="Enter current password"
                        data-testid="current-password-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>New Password *</label>
                      <input
                        type="password"
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                        required
                        placeholder="At least 8 characters"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Confirm New Password *</label>
                      <input
                        type="password"
                        value={passwordForm.confirm_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                        required
                        placeholder="Re-enter new password"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={() => {
                          setShowChangePassword(false);
                          setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                        }}
                        data-testid="cancel-change-password"
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary" disabled={changingPassword} data-testid="submit-change-password">
                        {changingPassword ? 'Saving...' : 'Update Password'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}

          {/* SCHEDULE TAB (current + past) */}
          {activeTab === 'schedule' && (
            <div className="schedule-section">
              <h2>Current Weekly Schedule</h2>
              {currentSchedule ? (
                <div className="schedule-card">
                  <div className="schedule-info">
                    <div className="schedule-icon">
                      <FileText size={48} />
                    </div>
                    <div>
                      <h3>{currentSchedule.filename}</h3>
                      <p>Active: {new Date(currentSchedule.week_start).toLocaleDateString()}{currentSchedule.week_end ? ` - ${new Date(currentSchedule.week_end).toLocaleDateString()}` : ''}</p>
                      <p className="schedule-uploaded">
                        Uploaded: {new Date(currentSchedule.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="schedule-actions">
                    <button onClick={() => viewSchedule(currentSchedule)} className="btn-view-schedule" data-testid="view-current-schedule-btn">
                      <Eye size={20} />
                      View Schedule
                    </button>
                    <button onClick={() => downloadSchedule(currentSchedule)} className="btn-download" data-testid="download-current-schedule-btn">
                      <Download size={20} />
                      Download
                    </button>
                  </div>
                </div>
              ) : (
                <div className="no-schedule">
                  <FileText size={64} />
                  <p>No current schedule available.</p>
                  <span>Check back later or contact your manager</span>
                </div>
              )}

              <div className="past-schedules-block" data-testid="past-schedules-section">
                <h2 className="past-schedules-heading">Past Schedules</h2>
                <p className="past-schedules-hint">Access the last 4 weeks of schedules.</p>
                {pastOnly.length > 0 ? (
                  <div className="past-schedules-list">
                    {pastOnly.map((sched) => (
                      <div key={sched.id} className="schedule-card" data-testid={`past-schedule-${sched.id}`}>
                        <div className="schedule-info">
                          <div className="schedule-icon">
                            <FileText size={40} />
                          </div>
                          <div>
                            <h3>{sched.filename}</h3>
                            <p>Active: {new Date(sched.week_start).toLocaleDateString()}{sched.week_end ? ` - ${new Date(sched.week_end).toLocaleDateString()}` : ''}</p>
                            <p className="schedule-uploaded">
                              Uploaded: {new Date(sched.uploaded_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="schedule-actions">
                          <button onClick={() => viewSchedule(sched)} className="btn-view-schedule">
                            <Eye size={18} />
                            View
                          </button>
                          <button onClick={() => downloadSchedule(sched)} className="btn-download">
                            <Download size={18} />
                            Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-schedule">
                    <History size={64} />
                    <p>No past schedules yet</p>
                    <span>Previous weekly schedules will appear here</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TIME OFF TAB */}
          {activeTab === 'timeoff' && (
            <div className="timeoff-section">
              <div className="section-header">
                <h2>Time Off Requests</h2>
                <button 
                  className="btn-primary"
                  onClick={() => setShowRequestForm(!showRequestForm)}
                >
                  {showRequestForm ? 'Cancel' : 'Request Time Off'}
                </button>
              </div>

              {showRequestForm && (
                <div className="request-form-card">
                  <h3>New Time Off Request</h3>
                  <form onSubmit={handleRequestSubmit} className="request-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Start Date *</label>
                        <input
                          type="date"
                          value={timeoffRequest.start_date}
                          onChange={(e) => setTimeoffRequest({...timeoffRequest, start_date: e.target.value})}
                          required
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div className="form-group">
                        <label>End Date *</label>
                        <input
                          type="date"
                          value={timeoffRequest.end_date}
                          onChange={(e) => setTimeoffRequest({...timeoffRequest, end_date: e.target.value})}
                          required
                          min={timeoffRequest.start_date || new Date().toISOString().split('T')[0]}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Reason (Optional)</label>
                      <textarea
                        value={timeoffRequest.reason}
                        onChange={(e) => setTimeoffRequest({...timeoffRequest, reason: e.target.value})}
                        rows="3"
                        placeholder="Vacation, personal, medical, etc."
                      />
                    </div>
                    <button type="submit" className="btn-primary">
                      <Send size={20} />
                      Submit Request
                    </button>
                  </form>
                </div>
              )}

              <div className="requests-list">
                <h3>My Requests</h3>
                {myTimeoffRequests.length === 0 ? (
                  <p className="no-data">No time-off requests yet</p>
                ) : (
                  myTimeoffRequests.map((request) => (
                    <div key={request.id} className="request-card">
                      <div className="request-info">
                        <p><strong>Dates:</strong> {request.start_date} to {request.end_date}</p>
                        {request.reason && <p><strong>Reason:</strong> {request.reason}</p>}
                        <p className="request-date">
                          Submitted: {new Date(request.created_at).toLocaleDateString()}
                        </p>
                        {request.reviewed_at && (
                          <p className="request-reviewed">
                            Reviewed: {new Date(request.reviewed_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <span className={`status-badge status-${request.status}`}>
                        {request.status}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* SHIFT SWAP TAB */}
          {activeTab === 'shiftswap' && (
            <div className="timeoff-section">
              <div className="section-header">
                <h2>Shift Swap Requests</h2>
                <button 
                  className="btn-primary"
                  onClick={() => setShowShiftSwapForm(!showShiftSwapForm)}
                >
                  {showShiftSwapForm ? 'Cancel' : 'Request Shift Swap'}
                </button>
              </div>

              {showShiftSwapForm && (
                <div className="request-form-card">
                  <h3>New Shift Swap Request</h3>
                  <form onSubmit={handleShiftSwapSubmit} className="request-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Shift Date *</label>
                        <input
                          type="date"
                          value={shiftSwapRequest.shift_date}
                          onChange={(e) => setShiftSwapRequest({...shiftSwapRequest, shift_date: e.target.value})}
                          required
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div className="form-group">
                        <label>Shift Time *</label>
                        <input
                          type="text"
                          value={shiftSwapRequest.shift_time}
                          onChange={(e) => setShiftSwapRequest({...shiftSwapRequest, shift_time: e.target.value})}
                          required
                          placeholder="e.g., 5:00 PM - 11:00 PM"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Swap With (Optional)</label>
                      <input
                        type="text"
                        value={shiftSwapRequest.swap_with}
                        onChange={(e) => setShiftSwapRequest({...shiftSwapRequest, swap_with: e.target.value})}
                        placeholder="Employee name (if arranged)"
                      />
                    </div>
                    <div className="form-group">
                      <label>Notes (Optional)</label>
                      <textarea
                        value={shiftSwapRequest.notes}
                        onChange={(e) => setShiftSwapRequest({...shiftSwapRequest, notes: e.target.value})}
                        rows="3"
                        placeholder="Any additional details..."
                      />
                    </div>
                    <button type="submit" className="btn-primary">
                      <Send size={20} />
                      Submit Request
                    </button>
                  </form>
                </div>
              )}

              <div className="requests-list">
                <h3>My Requests</h3>
                {myShiftSwapRequests.length === 0 ? (
                  <p className="no-data">No shift swap requests yet</p>
                ) : (
                  myShiftSwapRequests.map((request) => (
                    <div key={request.id} className="request-card">
                      <div className="request-info">
                        <p><strong>Date:</strong> {request.shift_date}</p>
                        <p><strong>Time:</strong> {request.shift_time}</p>
                        {request.swap_with && <p><strong>Swap with:</strong> {request.swap_with}</p>}
                        {request.notes && <p><strong>Notes:</strong> {request.notes}</p>}
                        <p className="request-date">
                          Submitted: {new Date(request.created_at).toLocaleDateString()}
                        </p>
                        {request.reviewed_at && (
                          <p className="request-reviewed">
                            Reviewed: {new Date(request.reviewed_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <span className={`status-badge status-${request.status}`}>
                        {request.status}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ANNOUNCEMENTS TAB */}
          {activeTab === 'announcements' && (
            <div className="announcements-section">
              <h2>Announcements</h2>
              <div className="announcements-list">
                {announcements.length === 0 ? (
                  <p className="no-data">No announcements yet</p>
                ) : (
                  announcements.map((announcement) => (
                    <div key={announcement.id} className="announcement-card">
                      <h3>{announcement.title}</h3>
                      <p className="announcement-meta">
                        By {announcement.created_by_name} • {new Date(announcement.created_at).toLocaleDateString()}
                      </p>
                      <p className="announcement-message">{announcement.message}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* TEAM FEED TAB */}
          {activeTab === 'feed' && (
            <div className="feed-section">
              <h2>Team Feed</h2>
              
              <div className="create-post-card">
                <form onSubmit={handleCreatePost} className="create-post-form">
                  <textarea
                    value={postContent}
                    onChange={(e) => setPostContent(e.target.value)}
                    placeholder="Share an update with the team..."
                    maxLength="500"
                    rows="3"
                  />
                  <div className="post-actions">
                    <label className="image-upload-btn">
                      <ImageIcon size={20} />
                      {postImage ? postImage.name : 'Add Photo'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setPostImage(e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button type="submit" className="btn-primary" disabled={!postContent && !postImage}>
                      Post Update
                    </button>
                  </div>
                  {postImage && (
                    <div className="image-preview">
                      <span>{postImage.name}</span>
                      <button type="button" onClick={() => setPostImage(null)}>
                        X
                      </button>
                    </div>
                  )}
                </form>
              </div>

              <div className="posts-list">
                {posts.map((post) => (
                  <div key={post.id} className="post-card">
                    <div className="post-header">
                      <div>
                        <h4>{post.author_name}</h4>
                        <span className="post-time">{new Date(post.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    
                    {post.content && <p className="post-content">{post.content}</p>}
                    
                    {post.image_url && (
                      <img 
                        src={`${BACKEND_URL}${post.image_url}`} 
                        alt="Post" 
                        className="post-image"
                      />
                    )}
                    
                    <div className="post-stats">
                      <button 
                        onClick={() => handleToggleLike(post.id)}
                        className={`btn-like ${post.user_liked ? 'liked' : ''}`}
                      >
                        <Heart size={18} fill={post.user_liked ? 'currentColor' : 'none'} />
                        {post.like_count}
                      </button>
                      <button 
                        onClick={() => {
                          if (expandedComments[post.id]) {
                            setExpandedComments({ ...expandedComments, [post.id]: null });
                          } else {
                            loadComments(post.id);
                          }
                        }}
                        className="btn-comment"
                      >
                        <MessageSquare size={18} />
                        {post.comment_count}
                      </button>
                    </div>

                    {expandedComments[post.id] && (
                      <div className="comments-section">
                        <div className="add-comment">
                          <input
                            type="text"
                            placeholder="Add a comment..."
                            value={commentText[post.id] || ''}
                            onChange={(e) => setCommentText({ ...commentText, [post.id]: e.target.value })}
                            maxLength="250"
                          />
                          <button onClick={() => handleAddComment(post.id)}>Post</button>
                        </div>
                        <div className="comments-list">
                          {expandedComments[post.id]?.map((comment) => (
                            <div key={comment.id} className="comment">
                              <strong>{comment.author_name}</strong>
                              <p>{comment.content}</p>
                              <span className="comment-time">{new Date(comment.created_at).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeePortal;
